import { FC, ReactNode, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "data/data-provider";
import FleetCard from "components/FleetCard/FleetCard";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { CarListingModal } from "models/vehicleModal";

export interface SectionGridFeaturePlacesProps {
  heading?: ReactNode;
}

const SectionGridFeaturePlaces: FC<SectionGridFeaturePlacesProps> = ({
  heading = (
    <h2 className="text-4xl font-bold">
      <span className="text-red-600">Explore</span> All Vehicles
    </h2>
  ),
}) => {
  const { ourVehicles, ourVehiclesLoading } = useData();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);

  const slideLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const slideRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  const renderCard = (car: CarListingModal) => (
    <div key={car.id} className="flex-shrink-0 w-[600px]">
      <FleetCard data={car} className="rounded-2xl overflow-hidden shadow-lg" />
    </div>
  );

  return (
    <div className="relative px-6 lg:px-16">
      <div className="flex justify-between items-center mb-6">{heading}</div>

      <div className="relative">
        <div className="flex items-center">
          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-scroll scroll-smooth hide-scrollbar w-full"
          >
            {ourVehiclesLoading ? (
              <div className="flex justify-center items-center w-full">
                <div className="spinner-border animate-spin w-8 h-8 border-4 rounded-full border-t-transparent border-gray-300"></div>
              </div>
            ) : (
              ourVehicles
                ?.filter((car) => car?.fleet_class?.name !== "Event")
                .map(renderCard)
            )}
          </div>
        </div>

        <div className="flex gap-4 mt-4 justify-start">
          <button
            onClick={slideLeft}
            className="w-12 h-12 flex justify-center items-center border border-gray-300 rounded-full bg-white shadow-md"
          >
            <svg width="17" height="15" viewBox="0 0 17 15" fill="none">
              <path
                d="M16.537 7.25C16.537 7.74219 16.1503 8.12891 15.6933 8.12891H3.74016L8.41594 12.5586C8.7675 12.875 8.7675 13.4375 8.4511 13.7539C8.13469 14.1055 7.60735 14.1055 7.25579 13.7891L1.06829 7.88281C0.892505 7.70703 0.822192 7.49609 0.822192 7.25C0.822192 7.03906 0.892505 6.82812 1.06829 6.65234L7.25579 0.746094C7.60735 0.429688 8.13469 0.429688 8.4511 0.78125C8.7675 1.09766 8.7675 1.66016 8.41594 1.97656L3.74016 6.40625H15.6933C16.1855 6.40625 16.537 6.79297 16.537 7.25Z"
                fill="#FF0000"
              />
            </svg>
          </button>
          <button
            onClick={slideRight}
            className="w-12 h-12 flex justify-center items-center border border-gray-300 rounded-full bg-white shadow-md"
          >
            <svg width="17" height="15" viewBox="0 0 17 15" fill="none">
              <path
                d="M10.1034 0.746094L16.2909 6.65234C16.4667 6.82812 16.5722 7.03906 16.5722 7.28516C16.5722 7.49609 16.4667 7.70703 16.2909 7.88281L10.1034 13.7891C9.78704 14.1055 9.22454 14.1055 8.90813 13.7539C8.59172 13.4375 8.59172 12.875 8.94329 12.5586L13.6191 8.12891H1.66594C1.17375 8.12891 0.822192 7.74219 0.822192 7.28516C0.822192 6.79297 1.17375 6.44141 1.66594 6.44141H13.6191L8.94329 1.97656C8.59172 1.66016 8.59172 1.09766 8.90813 0.78125C9.22454 0.429688 9.75188 0.429688 10.1034 0.746094Z"
                fill="#FF0000"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* "View All Cars" Button */}
      <div className="flex justify-center mt-10">
        <ButtonPrimary
          className="bg-red-500 text-white px-6 py-3 rounded-full text-lg font-bold shadow-lg"
          onClick={() => {
            navigate("/vehicles");
          }}
        >
          View All Cars
        </ButtonPrimary>
      </div>
    </div>
  );
};

export default SectionGridFeaturePlaces;
